import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageShell = styled.div`
  min-height: calc(100vh - 60px);
  padding: 40px clamp(20px, 4vw, 56px);
  background: linear-gradient(135deg, #eef6ff 0%, #f8fbff 100%);
  overflow-x: hidden;

  @media (max-width: 640px) {
    padding: 22px 14px 72px;
  }
`;

const Header = styled.div`
  max-width: 980px;
  margin: 0 auto 24px;
  display: flex;
  align-items: center;
  gap: 18px;

  @media (max-width: 520px) {
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 18px;
  }
`;

const HeaderIcon = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%);
  box-shadow: 0 16px 35px rgba(37, 99, 235, 0.25);
  flex-shrink: 0;

  @media (max-width: 520px) {
    width: 48px;
    height: 48px;
    border-radius: 15px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const HeaderCopy = styled.div`
  h1 {
    margin: 0 0 6px;
    color: #0f2d4e;
    font-size: clamp(2rem, 4vw, 3rem);
    line-height: 1.05;
    font-weight: 800;
  }

  p {
    margin: 0;
    color: #64748b;
    font-size: 1rem;
    line-height: 1.55;
  }

  @media (max-width: 520px) {
    h1 { font-size: clamp(1.55rem, 9vw, 2rem); }
    p { font-size: 0.92rem; }
  }
`;

const Card = styled.div`
  max-width: 980px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  animation: ${fadeIn} 300ms ease-out;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormCard = styled.form`
  background: #ffffff;
  border: 1px solid #dbeafe;
  border-radius: 22px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
  padding: clamp(20px, 4vw, 40px);

  @media (max-width: 520px) {
    border-radius: 18px;
  }
`;

const SideCard = styled.aside`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.06);
  padding: clamp(20px, 4vw, 28px);
  height: fit-content;

  h2 {
    margin: 0 0 14px;
    color: #102a43;
    font-size: 1.1rem;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const Tip = styled.li`
  display: flex;
  gap: 10px;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.45;

  span {
    color: #0ea5e9;
    font-weight: 800;
    flex-shrink: 0;
  }
`;

const FieldGroup = styled.div`
  margin-bottom: 22px;

  &:focus-within label {
    color: #0f75bc;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #334155;
  font-size: 0.92rem;
  font-weight: 700;
`;

const PasswordWrap = styled.div`
  position: relative;
  isolation: isolate;
`;

const FieldIcon = styled.span`
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #0ea5e9;
  background: linear-gradient(135deg, rgba(14, 165, 233, 0.14), rgba(37, 99, 235, 0.08));
  pointer-events: none;
  transition: color 160ms, background 160ms, transform 160ms;
  z-index: 1;

  ${PasswordWrap}:focus-within & {
    color: #ffffff;
    background: linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%);
    transform: translateY(-50%) scale(1.04);
  }
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1.5px solid #c7d5e8;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  color: #0f172a;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 17px 56px 17px 58px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 10px 24px rgba(15, 45, 78, 0.06);
  transition: border-color 160ms, box-shadow 160ms, background 160ms, transform 160ms;

  &::placeholder {
    color: #94a3b8;
    font-weight: 500;
    letter-spacing: 0;
  }

  &:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.13), 0 16px 34px rgba(14, 165, 233, 0.12);
    background: #ffffff;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    background: #f8fafc;
    color: #94a3b8;
    box-shadow: none;
    transform: none;
  }

  @media (max-width: 420px) {
    font-size: 0.95rem;
    padding: 15px 50px 15px 52px;
    border-radius: 16px;
  }

  ${props => props.$error && css`
    border-color: #ef4444;
    background: linear-gradient(180deg, #fffafa 0%, #fef2f2 100%);
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.11);
  `}
`;

const ToggleButton = styled.button`
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 12px;
  background: #eef6ff;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 160ms, color 160ms, transform 160ms;
  cursor: pointer;

  &:hover {
    background: #dbeafe;
    color: #0f75bc;
    transform: translateY(-50%) scale(1.04);
  }

  &:focus-visible {
    outline: 3px solid rgba(14, 165, 233, 0.25);
    outline-offset: 2px;
  }
`;

const RequirementList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 14px 0 4px;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const Requirement = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.$met ? '#047857' : '#94a3b8'};
  font-size: 0.84rem;

  span {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: ${props => props.$met ? '#10b981' : '#cbd5e1'};
    color: #ffffff;
    font-size: 0.7rem;
    font-weight: 800;
  }
`;

const InlineError = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 22px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.92rem;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;

  @media (max-width: 520px) {
    flex-direction: column-reverse;
    margin-top: 24px;
  }
`;

const SecondaryButton = styled.button`
  border: 1.5px solid #cbd5e1;
  border-radius: 14px;
  padding: 13px 18px;
  background: #ffffff;
  color: #334155;
  font-weight: 700;
  cursor: pointer;
  min-height: 46px;

  @media (max-width: 520px) {
    width: 100%;
  }

  &:hover:not(:disabled) {
    background: #f8fafc;
  }
`;

const PrimaryButton = styled.button`
  border: 0;
  border-radius: 14px;
  padding: 13px 22px;
  background: linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%);
  color: #ffffff;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 12px 26px rgba(37, 99, 235, 0.24);
  min-height: 46px;

  @media (max-width: 520px) {
    width: 100%;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const Icons = {
  Lock: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  Eye: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  EyeOff: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ),
  Key: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.78 7.78 5.5 5.5 0 0 1 7.78-7.78zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  ),
  Alert: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
};

const PasswordField = ({ id, label, value, onChange, disabled, error, autoComplete, placeholder }) => {
  const [visible, setVisible] = useState(false);

  return (
    <FieldGroup>
      <Label htmlFor={id}>{label}</Label>
      <PasswordWrap>
        <FieldIcon aria-hidden="true">
          <Icons.Key />
        </FieldIcon>
        <Input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          disabled={disabled}
          $error={error}
          autoComplete={autoComplete}
          placeholder={placeholder}
        />
        <ToggleButton
          type="button"
          onClick={() => setVisible(current => !current)}
          aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
        >
          {visible ? <Icons.EyeOff /> : <Icons.Eye />}
        </ToggleButton>
      </PasswordWrap>
    </FieldGroup>
  );
};

const ChangePassword = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requirements = useMemo(() => ({
    minLength: newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasLowercase: /[a-z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
  }), [newPassword]);

  const passwordIsValid = Object.values(requirements).every(Boolean);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const formIsValid = currentPassword.length > 0 && passwordIsValid && passwordsMatch;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!formIsValid) {
      setError('Please complete all fields and meet the password requirements.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Unable to change password.');
      }

      toast.success('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      navigate('/profile');
    } catch (submitError) {
      setError(submitError.message || 'Unable to change password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell>
      <Header>
        <HeaderIcon>
          <Icons.Lock />
        </HeaderIcon>
        <HeaderCopy>
          <h1>Change Password</h1>
          <p>Update the password you use to sign in to your practitioner account.</p>
        </HeaderCopy>
      </Header>

      <Card>
        <FormCard onSubmit={handleSubmit}>
          {error && (
            <InlineError>
              <Icons.Alert />
              {error}
            </InlineError>
          )}

          <PasswordField
            id="currentPassword"
            label="Current password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            disabled={isSubmitting}
            autoComplete="current-password"
            placeholder="Enter your current password"
          />

          <PasswordField
            id="newPassword"
            label="New password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            disabled={isSubmitting}
            autoComplete="new-password"
            placeholder="Create a strong new password"
          />

          <RequirementList aria-label="Password requirements">
            <Requirement $met={requirements.minLength}><span>{requirements.minLength ? '✓' : '—'}</span>At least 8 characters</Requirement>
            <Requirement $met={requirements.hasUppercase}><span>{requirements.hasUppercase ? '✓' : '—'}</span>One uppercase letter</Requirement>
            <Requirement $met={requirements.hasLowercase}><span>{requirements.hasLowercase ? '✓' : '—'}</span>One lowercase letter</Requirement>
            <Requirement $met={requirements.hasNumber}><span>{requirements.hasNumber ? '✓' : '—'}</span>One number</Requirement>
          </RequirementList>

          <PasswordField
            id="confirmPassword"
            label="Confirm new password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            disabled={isSubmitting}
            error={confirmPassword.length > 0 && !passwordsMatch}
            autoComplete="new-password"
            placeholder="Re-enter your new password"
          />

          <Actions>
            <SecondaryButton type="button" onClick={() => navigate(-1)} disabled={isSubmitting}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={isSubmitting || !formIsValid}>
              {isSubmitting ? 'Changing password…' : 'Change Password'}
            </PrimaryButton>
          </Actions>
        </FormCard>

        <SideCard>
          <h2>Password tips</h2>
          <ul>
            <Tip><span>✓</span>Use a unique password that you do not use on other websites.</Tip>
            <Tip><span>✓</span>Mix uppercase letters, lowercase letters, and numbers.</Tip>
            <Tip><span>✓</span>After changing it, use the new password the next time you sign in.</Tip>
          </ul>
        </SideCard>
      </Card>
    </PageShell>
  );
};

export default ChangePassword;
